import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ClerkProvider, SignedIn, SignedOut, RedirectToSignIn } from '@clerk/clerk-react';
import { CacheProvider } from './contexts/CacheContext';
import Layout from './components/Layout';
import LandingPage from './components/LandingPage';
import SignInPage from './components/SignInPage';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ProjectDetails from './pages/ProjectDetails';
import RunHistory from './pages/RunHistory';
import SystemResources from './pages/SystemResources';
import CostAnalyticsPage from './pages/CostAnalyticsPage';
import QueryAnalyticsPage from './pages/QueryAnalyticsPage';
import LogAnalyticsPage from './pages/LogAnalyticsPage';
import OrganizationManagementPage from './pages/OrganizationManagement';
import ApiKeyManagementPage from './pages/ApiKeyManagementPage';
import OrganizationRouteGuard from './components/OrganizationRouteGuard';
import getClerkConfig from './config/clerk';
import ClerkErrorBoundary from './components/ClerkErrorBoundary';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  return (
    <>
      <SignedIn>
        {children}
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
};

function App() {
  const clerkConfig = getClerkConfig();

  return (
    <ClerkErrorBoundary>
      <ClerkProvider 
        publishableKey={clerkConfig.publishableKey}
        // Apply the comprehensive configuration
        {...clerkConfig}
      >
        <CacheProvider>
          <Router>
            <div className="h-screen bg-gray-50">
              <Routes>
                {/* Sign-in page as the starting point */}
                <Route path="/" element={
                  <>
                    <SignedOut>
                      <SignInPage />
                    </SignedOut>
                    <SignedIn>
                      <Navigate to="/dashboard" replace />
                    </SignedIn>
                  </>
                } />
                
                {/* Protected routes with Layout wrapper */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <div className="h-screen flex flex-col">
                      <Layout />
                    </div>
                  </ProtectedRoute>
                }>
                  <Route path="dashboard" element={
                    <OrganizationRouteGuard>
                      <Dashboard />
                    </OrganizationRouteGuard>
                  } />
                  <Route path="projects" element={
                    <OrganizationRouteGuard>
                      <Projects />
                    </OrganizationRouteGuard>
                  } />
                  <Route path="projects/:projectId" element={
                    <OrganizationRouteGuard>
                      <ProjectDetails />
                    </OrganizationRouteGuard>
                  } />
                  <Route path="projects/:projectId/runhistory" element={
                    <OrganizationRouteGuard>
                      <RunHistory />
                    </OrganizationRouteGuard>
                  } />
                  <Route path="system-resources" element={
                    <OrganizationRouteGuard>
                      <SystemResources />
                    </OrganizationRouteGuard>
                  } />
                  <Route path="cost-analytics" element={
                    <OrganizationRouteGuard>
                      <CostAnalyticsPage />
                    </OrganizationRouteGuard>
                  } />
                  <Route path="query-analytics" element={
                    <OrganizationRouteGuard>
                      <QueryAnalyticsPage />
                    </OrganizationRouteGuard>
                  } />
                  <Route path="log-analytics" element={
                    <OrganizationRouteGuard>
                      <LogAnalyticsPage />
                    </OrganizationRouteGuard>
                  } />
                  <Route path="organization" element={<OrganizationManagementPage />} />
                  <Route path="api-keys" element={
                    <OrganizationRouteGuard>
                      <ApiKeyManagementPage />
                    </OrganizationRouteGuard>
                  } />
                </Route>
                              </Routes>
              </div>
            </Router>
          </CacheProvider>
      </ClerkProvider>
    </ClerkErrorBoundary>
  );
}

export default App; 
